// Loaders that populate `state` from Supabase, and the session bootstrap.
import { loadAdminStatistics } from "./admin.js";
import { fetchAllRPC } from "../lib/api.js";
import { showToast } from "../lib/dom.js";
import { render } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";

export async function loadAccount() {
  const [
    { data: account, error: accountError },
    { data: profiles, error: profilesError },
    { data: roles, error: rolesError }
  ] = await Promise.all([
    supabase.from("account_profiles").select("*").single(),
    supabase.from("child_profiles").select("*").order("created_at"),
    supabase.rpc("list_account_roles")
  ]);
  if (accountError) throw accountError;
  if (profilesError) throw profilesError;
  if (rolesError) throw rolesError;
  state.account = account;
  state.roles = (roles || []).map(item => item.role);
  state.profiles = profiles || [];
  await supabase.rpc("apply_due_school_promotions");
  const [{ data: schoolClasses }, { data: enrollments }, { data: academicYears }, { data: isAdmin }, { data: learningHierarchy }, gradeRangesResult] = await Promise.all([
    supabase.rpc("list_school_classes"),
    supabase.rpc("list_child_school_enrollments"),
    supabase.rpc("list_academic_years"),
    supabase.rpc("is_current_admin"),
    supabase.rpc("list_learning_hierarchy", { selected_language: state.language }),
    fetchAllRPC("list_catalog_exercise_grade_ranges")
  ]);
  state.schoolClasses = schoolClasses || [];
  state.enrollments = enrollments || [];
  state.academicYears = academicYears || [];
  state.isAdmin = isAdmin === true;
  state.learningHierarchy = learningHierarchy || [];
  state.catalogGradeRanges = Object.fromEntries((gradeRangesResult.data || []).map(item => [item.exercise_id, item]));
  if (state.isAdmin) {
    state.adminMode = true;
    state.teacherMode = false;
    state.profile = null;
    localStorage.removeItem("brainmath-profile");
    await loadAdminStatistics();
    return;
  }
  const learnerOnly = state.roles.includes("learner") && !state.roles.includes("parent") && !state.roles.includes("teacher");
  if (learnerOnly && state.profiles.length) {
    state.profile = state.profiles[0];
    localStorage.setItem("brainmath-profile", state.profile.id);
    await loadExercises();
    state.teacherMode = false;
    state.adminMode = false;
    state.view = "home";
    return;
  }
  if (state.roles.includes("teacher")) {
    const [
      { data: classes },
      { data: teacherStudents },
      { data: teacherSchools },
      { data: teacherExercises },
      teacherCatalogResult,
      { data: aiWallet },
      { data: creditProducts }
    ] = await Promise.all([
      supabase.rpc("list_my_classes"),
      supabase.rpc("list_teacher_students"),
      supabase.rpc("list_teacher_schools"),
      supabase.rpc("list_teacher_exercises", { selected_language: state.language }),
      fetchAllRPC("list_teacher_catalog_exercises", { selected_language: state.language }),
      supabase.rpc("get_my_ai_wallet"),
      supabase.rpc("list_ai_credit_products")
    ]);
    state.classes = classes || [];
    state.teacherStudents = teacherStudents || [];
    state.teacherSchools = teacherSchools || [];
    state.teacherExercises = teacherExercises || [];
    state.teacherCatalogExercises = teacherCatalogResult.data || [];
    state.aiWallet = aiWallet?.[0] || { balance: 0, lifetime_purchased: 0, lifetime_used: 0 };
    state.creditProducts = creditProducts || [];
  }

  if (state.roles.includes("parent") && !state.teacherMode) {
    const childModeProfileID = sessionStorage.getItem("brainmath-parent-child-mode");
    const childModeProfile = state.profiles.find(profile => profile.id === childModeProfileID);
    if (childModeProfile) {
      state.parentMode = false;
      state.profile = childModeProfile;
      localStorage.setItem("brainmath-profile", childModeProfile.id);
      await loadExercises();
    } else {
      state.parentMode = true;
      state.profile = null;
      localStorage.removeItem("brainmath-profile");
      await loadParentCenterData();
    }
    return;
  }

  const storedProfile = localStorage.getItem("brainmath-profile");
  state.profile = state.profiles.find(profile => profile.id === storedProfile) || null;
  if (state.profile) await loadExercises();
}

export async function loadParentCenterData() {
  if (!state.roles.includes("parent")) return;
  const [overviewResult, activityResult, learningResult] = await Promise.all([
    supabase.rpc("list_parent_child_overview"),
    supabase.rpc("list_parent_child_activity", { selected_language: state.language, selected_limit: 250 }),
    supabase.rpc("list_parent_learning_results", { selected_language: state.language, selected_limit: 500 })
  ]);
  if (overviewResult.error) throw overviewResult.error;
  if (activityResult.error) throw activityResult.error;
  if (learningResult.error) throw learningResult.error;
  state.parentOverview = overviewResult.data || [];
  state.parentActivity = activityResult.data || [];
  state.parentResults = learningResult.data || [];
  const selectedStillExists = state.parentOverview.some(item => item.child_profile_id === state.parentSelectedChild);
  if (!selectedStillExists) state.parentSelectedChild = state.parentOverview[0]?.child_profile_id || state.profiles[0]?.id || null;
}

export async function loadExercises() {
  if (!state.profile) {
    state.exercises = [];
    return;
  }
  const { data, error } = await supabase.rpc("list_pending_exercises", {
    selected_child_profile: state.profile.id,
    selected_language: state.language
  });
  if (error) throw error;
  state.exercises = data || [];
}

async function recordAppConnection() {
  if (!state.session?.user?.id) return;
  const storageKey = `brainmath-connection-${state.session.user.id}`;
  const lastRecordedAt = Number(sessionStorage.getItem(storageKey) || 0);
  if (Date.now() - lastRecordedAt < 30 * 60 * 1000) return;
  const { error } = await supabase.rpc("record_app_connection", {
    selected_client: "web",
    selected_event_type: "app_open",
    selected_language: state.language
  });
  if (!error) sessionStorage.setItem(storageKey, String(Date.now()));
}

export async function restore() {
  const { data } = await supabase.auth.getSession();
  state.session = data.session;
  if (state.session) {
    try {
      await loadAccount();
      await recordAppConnection();
    } catch (error) {
      showToast(error.message);
    }
  }
  render();
  const purchaseStatus = new URLSearchParams(location.search).get("credit_purchase");
  if (purchaseStatus) {
    history.replaceState({}, "", location.pathname);
    setTimeout(() => {
      showToast(purchaseStatus === "success"
        ? (state.language === "it"
            ? "Pagamento ricevuto. I crediti appariranno appena Stripe conferma l’acquisto."
            : "Payment received. Credits will appear as soon as Stripe confirms the purchase.")
        : (state.language === "it" ? "Acquisto annullato." : "Purchase cancelled."));
    }, 250);
  }
}
